/**
 * shared/voice/policy.ts — every number this product turns on, with the thing it was derived from.
 *
 * ════════════════════════════════════════════════════════════════════════════════════════════════
 * **A constant without a derivation is a bug.** Every value below carries either a file-and-line
 * citation into Chatterbox's source (read on disk at
 * `/home/kartios/rocm-torch/venv/lib/python3.12/site-packages/chatterbox/`, 2026-08-07), a measured
 * number from the boxes, or an explicit note that it is a stated bound awaiting a probe. There is
 * exactly one kind of constant that may exist without a measurement, and it says so in capitals.
 * ════════════════════════════════════════════════════════════════════════════════════════════════
 *
 * ── VOX-LAW 31, and why this file has no loudness term anywhere ─────────────────────────────────
 *
 * `tts_turbo.py:204-215` defines `norm_loudness(self, wav, sr, target_lufs=-27)`, and
 * `prepare_conditionals(..., norm_loudness=True)` (`:217`, `:223-224`) calls it on the reference **by
 * default, before any slicing**, as a plain linear gain with no limiter. Chatterbox turbo therefore
 * already normalises every reference to −27 LUFS itself.
 *
 * Three consequences, all of which DELETE work rather than adding it:
 *   1. Our own −27 pass would be a ~0 dB no-op.
 *   2. Reaching it through a soft-knee limiter would apply real knee distortion to hit a target the
 *      model then re-measures and re-applies.
 *   3. Any weight on level or LUFS-match is a term with **zero causal path to clone quality**, and a
 *      monotone-maximised composite will trade real quality for it — two candidate windows, one
 *      1.5 dB quieter but cleaner, and the loudness term penalises the cleaner one.
 *
 * So `OBJECTIVE_WEIGHTS` has no loudness key, `ObjectiveTerm` is a closed union that cannot express
 * one, and `shared/audio/loudness.ts` deliberately does not export a gain planner at all. If you are
 * about to add a level term because the seeds look uneven on `/admin`, read §5.3 first: they are
 * uneven, the model equalises them, and the evenness you would be buying is cosmetic.
 *
 * ── The one thing that would reopen all of it ───────────────────────────────────────────────────
 *
 * If PROBE-2f ever shows the FastAPI wrapper passes `norm_loudness=False`, seed level becomes
 * load-bearing again — **and that is one line of `SEED_POLICY`**, which is what that object is for.
 */

// ─── what the model reads, from source ───────────────────────────────────────

/** `models/s3gen/const.py:1` — `S3GEN_SR = 24000`. The rate the S3Gen decoder's reference is at. */
export const S3GEN_SR = 24_000;
/** `models/s3tokenizer/s3tokenizer.py:15` — `S3_SR = 16_000`. The rate the T3 token prompt is at. */
export const S3_SR = 16_000;

/**
 * The three conditioning horizons, in seconds, read out of `tts_turbo.py:111-112`:
 *
 *     ENC_COND_LEN = 15 * S3_SR      →  the T3 prompt sees the first 15.000 s @ 16 kHz  (`:234`)
 *     DEC_COND_LEN = 10 * S3GEN_SR   →  S3Gen sees the first 10.000 s @ 24 kHz          (`:228`)
 *     embeds_from_wavs([ref_16k_wav])→  the voice encoder sees the WHOLE FILE, unsliced (`:238`)
 *
 * The draft modelled two zones and computed that "extra audio moves 1 of ~376 conditioning tokens".
 * That described a mechanism that does not exist: audio past 15 s does not move a token, it moves the
 * *speaker embedding*, by mean-pooling — and the draft drew its boundary at 10 s while using 375 as
 * its denominator, which is the 15 s number.
 *
 * All three slices are **naive prefix slices** (`wav[:N]`). There is no VAD, no energy gating and no
 * best-segment selection anywhere in `prepare_conditionals`. Whatever sits at the head of the file is
 * what conditions the model, which is why VOX-LAW 32 makes trim run before the Zone A cut.
 */
export const ZONE_A_END_SECONDS = 10.0;
export const ZONE_B_END_SECONDS = 15.0;

/**
 * `librosa.effects.trim(wav, top_db=20)` at `models/voice_encoder/voice_encoder.py:266-267`.
 *
 * Recorded because it is the reason leading silence is an *invisible* defect: the voice-encoder path
 * is trimmed and the S3Gen and T3 paths are not (`tts_turbo.py:228`, `:234` slice raw), so any
 * similarity metric computed with the voice encoder cannot see the damage that 200 ms of room tone
 * at the head of a seed does to the other two paths. VOX-LAW 9 refuses that encoder as a scorer for
 * a separate reason; this is the second one.
 */
export const VOICE_ENCODER_TRIM_TOP_DB = 20;

// ─── the seed ────────────────────────────────────────────────────────────────

export interface SeedPolicy {
  /** Refuse to assemble a seed shorter than this, in seconds. See the derivation below. */
  minSeconds: number;
  /** What the studio aims to deliver. */
  targetSeconds: number;
  /** Hard ceiling on delivered length. */
  maxSeconds: number;
  /** VOX-LAW 3 / 3b: one continuous take up to here. */
  zoneAEndSeconds: number;
  zoneBEndSeconds: number;
  /** VOX-LAW 3c: may Zone C be assembled from other takes of the same speaker? */
  zoneCEnabled: boolean;
  /** The delivered file's depth. 24-bit is for the HEADROOM at capture; see §4.5. */
  deliveredBitDepth: 16;
  /** VOX-LAW 31. Peak, never loudness. */
  deliveredPeakDbfs: number;
  /** VOX-LAW 31, stated as a field so a future reader has to change a value rather than add a call. */
  loudnessNormalise: false;
  /** VOX-LAW 25's corollary. Native capture rate, no resampling by default; soxr is opt-in. */
  resample: "none" | "soxr";
  /** §12.2 exposure 2. spark-1's voice registry has no known DELETE verb. */
  maxPublishedRevsPerVoice: number;
}

export const SEED_POLICY: Readonly<SeedPolicy> = Object.freeze({
  /**
   * **6.0 s, and the 1.0 s of margin above the model's own floor is the whole point.**
   *
   * `tts_turbo.py:221`:  `assert len(s3gen_ref_wav) / _sr > 5.0, "Audio prompt must be longer than 5
   * seconds!"` — a STRICT inequality, evaluated after `librosa.load` at 24 kHz, before anything else.
   * A seed measuring exactly 5.0 s, or one a caller trims to 4.9 s, raises `AssertionError` inside
   * `prepare_conditionals`. VOX-LAW 15 promises every failure path ends in a seed; that one ends in a
   * stack trace on a machine Karti's mother cannot see, at the moment she has finished recording.
   *
   * The margin covers two things we do not control: Chatterbox's own `librosa.load` resampling
   * boundary (a 6.000 s file can load as 5.9999 s), and the voice encoder's `top_db=20` trim, which
   * runs on its own copy but proves the library is willing to shorten what we send. 6.0 is confirmed
   * against the real wrapper under PROBE-2g; if the wrapper adds a pass of its own, this number goes
   * up and nothing else changes.
   */
  minSeconds: 6.0,

  /**
   * 15.0 s — the T3 horizon (`ENC_COND_LEN`). Below it, part of the LM's style prefix is padding;
   * above it, only the mean-pooled speaker embedding moves at all.
   */
  targetSeconds: 15.0,

  /**
   * Also 15.0, and this is a **deliberate refusal to exploit Zone C yet**.
   *
   * §5.2 is right that Zone C is a cheap relaxation — the voice encoder mean-pools over the whole
   * file, so extra material moves the embedding a little and cannot be corrupted by a seam. But
   * "moves it a little" is a prediction, and PROBE-2e is the three-minute experiment that turns it
   * into a number (two seeds identical through second 15.000, 20 renders each, ECAPA cosine to
   * held-out). Shipping a longer seed before that measurement means every early voice in the family's
   * library was made under a policy nobody checked, and those are the voices that cannot be re-made.
   */
  maxSeconds: 15.0,
  zoneAEndSeconds: ZONE_A_END_SECONDS,
  zoneBEndSeconds: ZONE_B_END_SECONDS,
  zoneCEnabled: false,

  /**
   * The delivered seed is **16-bit**, written after windowing. Capture is 24-bit (VOX-LAW 1) and that
   * is not a contradiction: the extra eight bits are for HEADROOM, not for the model. `librosa.load`
   * converts to float32 and the seed is then gain-scaled and mel-transformed, so 24 bits contribute
   * nothing to seed fidelity — but a non-technical person recording for the first time will
   * occasionally shout a line, and a clipped take from someone who will not sit down a second time is
   * unrecoverable. By the time we write the seed the headroom has done its job, and VOX-LAW 6 hashes
   * what we deliver.
   */
  deliveredBitDepth: 16,

  /**
   * **−3 dBFS, peak, and never loudness (VOX-LAW 31).**
   *
   * −3 rather than −1 so that Chatterbox's own linear gain to −27 LUFS has room to work without
   * anything of ours engaging: `norm_loudness` applies `10^(gain_db/20)` with **no limiter**
   * (`tts_turbo.py:204-215`), so a seed already at −1 dBFS that needs +2 dB of its gain clips inside
   * the model, in float, silently. 3 dB of headroom covers the measured spread of speech crest
   * factors without costing anything: the seed is 16-bit and −3 dBFS still leaves ~93 dB of range
   * above a living room's −40 to −55 dBFS floor (§4.7).
   */
  deliveredPeakDbfs: -3,
  loudnessNormalise: false,

  /**
   * No resampling by default. §9.2's VOX-LAW 25 corollary: **if soxr or ffmpeg ever runs on delivered
   * seed bytes, its version enters the seed envelope**, because VOX-LAW 6 makes the delivered bytes
   * the rev — and a dependency bump would otherwise arrive in audiobook as a demolition charge
   * through the back door. Chatterbox resamples to 24 kHz itself on load, so doing it here buys
   * nothing and costs a version constant in an identity.
   */
  resample: "none",

  /**
   * ⚠️ **A STATED BOUND, NOT A MEASUREMENT.** PROBE-2d asks whether spark-1's voice registry has any
   * delete or unregister verb at all; today we do not know of one, so every rev of every family voice
   * accumulates forever on the box whose watchdog kills the service when `MemAvailable` falls —
   * taking audiobook's narration with it (§8.1, §12.2).
   *
   * 8 is derived from the only number we have: §8.5's canary budget implies ~24 voices ≈ one year of
   * a family, and 24 × 8 = 192 registry entries is a size the box has been observed to hold. If
   * PROBE-2d finds a delete verb this cap is deleted; if it finds none, retirement is a documented
   * manual `kuda scene activate 115` and this number is what stops it becoming weekly.
   */
  maxPublishedRevsPerVoice: 8,
});

// ─── capture ─────────────────────────────────────────────────────────────────

/**
 * The capture chain's shape. **VOX-LAW 1: `MediaRecorder` appears nowhere in the tree** — it gives
 * Opus in a container at a bitrate the browser picks, which is lossy compression applied to the one
 * artifact in this product whose fidelity is the entire point.
 */
export const CAPTURE = Object.freeze({
  /** §4.5, refused as a savings target. See `SEED_POLICY.deliveredBitDepth` for the other half. */
  bitDepth: 24 as const,
  channels: 1,
  /** An `AudioWorkletProcessor`'s `process()` quantum is fixed at 128 frames by the spec. Stated, not chosen. */
  quantumFrames: 128,
  /**
   * ~1 s per `postMessage` to the encode worker.
   *
   * The trade is message overhead against how much audio a dropped message loses. At 48 kHz mono
   * float32 a 1 s chunk is 192 kB, transferred (not copied), so the cost per message is a pointer;
   * 375 quanta per message is far above the rate at which structured-clone overhead matters. Smaller
   * chunks would make the drain-lag metric finer-grained and buy nothing else.
   */
  chunkMs: 1000,
  /**
   * 20 Hz metering, computed **in the worklet** and posted to the main thread, which only draws.
   *
   * Not on the main thread, and the reason is arithmetic: `measureLoudness` allocates a full copy and
   * runs two biquads over the entire signal per call, so re-metering a growing take every frame is
   * O(n²) — and it returns `-Infinity` under 400 ms, i.e. a DEAD METER for the first 0.4 s of every
   * take, which is the part she is most anxious about. 20 Hz is above the ~15 Hz at which a level
   * meter stops looking like it is responding to the voice and below anything React would struggle
   * with. **VOX-LAW 13: these numbers are advisory and are never written to the database.**
   */
  meterHz: 20,
});

/**
 * **VOX-LAW 2. `{ideal: false}`, never `{exact: false}`.**
 *
 * In `MediaTrackConstraints`, `{exact: false}` is a `ConstraintBoolean` whose `exact` member makes
 * the constraint MANDATORY: a device or OS that cannot disable echo cancellation, noise suppression
 * or auto gain control rejects with `OverconstrainedError` and the studio never opens. That is the
 * normal case on iOS (Chrome-on-iOS is WKWebView with its own AGC) and on many Android builds —
 * precisely "their own device in Chrome by Karti's mom and dad". The draft's most important law would
 * have turned the most likely device into a total failure, and contradicted VOX-LAW 15 in the same
 * line.
 *
 * Spread this into `getUserMedia({ audio: { ...DSP_OFF, ... } })`. `test/wiring.test.ts` greps
 * `client/src/studio/` for the identifier `exact` and fails on a hit.
 *
 * The other half is VOX-LAW 2b, which lives in `seedEligible` on the take row rather than here: a
 * take whose `getSettings()` still reports any of the three is **seed-ineligible, not refused**. It
 * remains usable as held-out reference and for coaching, and the session still produces a seed and
 * still reveals.
 */
export const DSP_OFF = Object.freeze({
  echoCancellation: Object.freeze({ ideal: false }),
  noiseSuppression: Object.freeze({ ideal: false }),
  autoGainControl: Object.freeze({ ideal: false }),
});

/** The three flags whose reported state decides VOX-LAW 2b. Grepped by `test/wiring.test.ts`. */
export const DSP_FLAGS = ["echoCancellation", "noiseSuppression", "autoGainControl"] as const;
export type DspFlag = (typeof DSP_FLAGS)[number];

/**
 * **VOX-LAW 5.** The worklet's cumulative frame count is compared against elapsed
 * `context.currentTime` on every chunk; drift beyond this voids the take, and a voided take is never
 * repaired.
 *
 * 10 ms is 480 frames at 48 kHz — far above any scheduling jitter (a quantum is 2.7 ms) and far below
 * the ~30 ms at which a listener hears a discontinuity. The failure it catches produces **no error
 * anywhere**: on a phone, an incoming call or a screen lock suspends the `AudioContext` and the take
 * truncates or stalls silently, while VOX-LAW 3 requires Zone A to be one continuous 10.000 s region.
 */
export const MAX_CLOCK_DRIFT_MS = 10;

// ─── the room check (§3.4) ───────────────────────────────────────────────────

/**
 * Three seconds of room, and it is the most load-bearing minute in the product: it proves the app can
 * hear her, measures the noise floor that becomes every take's trim threshold, estimates reverb,
 * checks the speakers, and cross-checks the DSP flags.
 *
 * Three seconds because that is the shortest window that gives a stable median over 10 ms windows
 * (≈3000 windows at a 1 ms hop) while being short enough that "say anything at all" does not become a
 * performance. It is also exactly EBU R128's short-term integration time, so the room's level is
 * reported in a unit that means something.
 */
export const ROOM_CHECK_MS = 3000;

/**
 * The minimum pre-roll before a cue that a noise floor may be measured from.
 *
 * Capture runs continuously from the moment the prompt screen appears (§3.6), so pre-roll is free —
 * but the floor is a median over the pre-roll's 10 ms windows, and a median over fewer than a few
 * hundred windows moves with a single chair creak. 500 ms is 500 windows at the 1 ms hop; below that
 * the per-take threshold starts tracking events instead of the room.
 */
export const PREROLL_MIN_MS = 500;

/**
 * Where a room stops being a good place to record, in dBFS of measured floor.
 *
 * ⚠️ **A COACHING TRIGGER, NOT A GATE.** VOX-LAW 15: the gate never refuses to produce a voice.
 * Crossing this fires one line from the coach budget in the room check, before she has performed
 * anything, phrased about the room rather than about her.
 *
 * −45 dBFS: §4.7 measured that a laptop or phone microphone in an Indian home at conversational level
 * sits around −40 to −55 dBFS of room tone. A floor at or above −45 means the fan, the street or the
 * television is within ~25 dB of her speech, and `n_cfm_timesteps=2` (§5.2) will reproduce it rather
 * than average it out. This is the same number the draft used as a TRIM THRESHOLD, where it was
 * catastrophic; as a coaching trigger it is exactly the right magnitude, which is the whole reason it
 * is written here with that history attached.
 */
export const NOISY_ROOM_DBFS = -45;

// ─── the go-cue (§3.6) ───────────────────────────────────────────────────────

/**
 * **Three clearly separated descending pulses, 660 Hz → 550 Hz → 440 Hz, and the frequency
 * range is a refusal.**
 *
 * The engineering review proposed a 4 kHz click, on the grounds that a narrowband click is trivially
 * notchable out of a contaminated take. That is refused: **presbycusis takes 4 kHz first, and a cue a
 * 68-year-old cannot hear is not a cue.** Three descending mid-band pulses are the audible half of
 * a familiar 3–2–1 count and sit where her hearing is intact.
 *
 * Contamination is solved by sequencing and geometry instead. VOX-LAW 2 disables echo cancellation on
 * purpose, so a cue played through the laptop speaker *will* be in the microphone signal — but both
 * the cue and the capture are on the same `AudioContext` clock, so the cue's exact sample offset is
 * known. **VOX-LAW 4: the take region begins no earlier than `postCueGuardMs` after the cue's
 * envelope decays**, and `test/cue-isolation.test.ts` asserts that no take region in a fixture
 * session contains narrowband energy at these three frequencies.
 *
 * 120 ms per pulse, Hann-windowed: long enough for the pitch to be unambiguous (a 660 Hz tone needs
 * ~15 ms for pitch to be heard at all) and short enough that each pulse is a cue rather than an
 * alarm. Pulses begin one second apart, matching the visual 3–2–1 and §3.5's three-second cue budget.
 * Hann rather than a rectangular gate because a hard edge on a sine is a click with energy
 * everywhere — including at 4 kHz, where she cannot hear it and the narrowband detector cannot find
 * it.
 */
export const CUE = Object.freeze({
  toneHz: Object.freeze([660, 550, 440]),
  toneMs: 120,
  /** One pulse per second: the audio clock and visual 3–2–1 use this same interval. */
  pulseIntervalMs: 1000,
  /**
   * 300 ms after the envelope decays. Covers the room's own decay (a domestic RT60 is 300–600 ms, so
   * the tail is 30–40 dB down by 300 ms) plus the speaker cone's ring-down, which on a laptop is a
   * few milliseconds. Any region boundary may be extended BACKWARDS to first onset **provided no cue
   * energy falls inside the extension** — extending a region inside one continuous capture is not a
   * splice and does not violate VOX-LAW 3.
   */
  postCueGuardMs: 300,
});

// ─── the coach (§3.9) ────────────────────────────────────────────────────────

/**
 * **Two corrective utterances for the entire session. Ever.**
 *
 * The draft had a rate-limited priority queue of corrections aimed at a self-conscious 68-year-old.
 * Priority ordering implies serial delivery, so across ten prompts she could receive up to ten
 * distinct corrections; around the third she stops trusting herself, starts over-articulating, and
 * the prosody — the entire point of the seed — collapses. That is the giving-up moment and it
 * produces no error anywhere.
 *
 * 2 rather than 1 because there are exactly two conditions worth interrupting for that the room check
 * cannot have already caught: something new started making noise, and she has drifted away from the
 * microphone. 2 rather than 3 because a third correction is the one that lands as *you keep getting
 * this wrong*. When the budget is spent, corrections are logged for `/admin` and not shown.
 */
export const COACH_BUDGET = 2;

// ─── the wait and the reveal (§3.12) ─────────────────────────────────────────

/**
 * **75 seconds from the last recording to the first audio, p90. This is a number in the design and a
 * defect when breached.**
 *
 * Derivation, each term measured or budgeted separately:
 *   seed assembly on amd-server   ≈  2 s CPU
 *   `POST /voices`                ≈ 20 s budgeted — UNMEASURED, PROBE-2
 *   one reveal sentence, ~8 s of audio at the measured ~3.7× realtime ≈ 2.2 s of GPU,
 *     plus admission-queue wait (VOX-LAW 30), budgeted at 30 s
 *   transfer                      ≈  5 s
 *                                   ─────
 *                                    57 s + slack
 *
 * This is the slowest, most contended, most user-visible path in the product and it was the only one
 * with no target. When it is breached the answer is not a longer spinner: §3.12 says the wait screen
 * counts down in seconds and shows her her own waveforms, and §3.14's deferred path is a designed
 * outcome, not a failure.
 */
export const REVEAL_BUDGET_MS = 75_000;

/**
 * **The longest payoff sentence the helper may author (§3.13), in characters. 250.**
 *
 * ⚠️ It is 250 rather than a round 500 because that is where **the audio** stops being trustworthy,
 * not where a column stops fitting. PROBE-2, measured: Chatterbox *"degrades above ~250 chars; the
 * server chunks at 450"* — and `enqueueSpeak` in `server/src/jobs/pipeline.ts` has always sliced the
 * reveal to 250 before it reaches the host. So a 500-character bound on the input was a promise the
 * generator did not keep: the sentence somebody typed for his mother was cut off mid-word, in her
 * voice, on the one screen this product exists to reach, with nothing anywhere saying it had been.
 * The bound now sits where the truncation was, so the refusal happens in front of the person who can
 * shorten it. `PREVIEW_MAX_CHARS` in `server/src/routes/voices.ts` is the same number for the same
 * measurement, on the tuning path.
 *
 * A sentence that long is already far past §3.13's *"Karti, I'm proud of you"* — the length exists to
 * catch a paste, not to invite an essay.
 */
export const REVEAL_TEXT_MAX_CHARS = 250;

/** Where a session becomes a bad experience. §3.5 targets ≤ 7 min; Appendix D.1 falsifies at 9. */
export const SESSION_TARGET_MS = 7 * 60_000;
export const SESSION_FALSIFIER_MS = 9 * 60_000;

// ─── hard gates (§5.3) ───────────────────────────────────────────────────────

/**
 * **True peak is a hard gate, not a score term.**
 *
 * Clipping is real distortion that survives resampling, gain and mel extraction, so a clipped take is
 * REJECTED, not ranked — ranking it would let a composite trade a little clipping for a better
 * DNSMOS. The measurement needs ≥4× oversampling through an interpolating filter, which exists
 * nowhere in audiobook's `shared/`, so `truePeakDbtp()` is written here as a new component and its
 * purpose is stated: **inter-sample peaks that only become audible after Chatterbox's own resample.**
 *
 * −0.1 dBTP: 24-bit capture has ~144 dB of range, so nothing a person says legitimately approaches
 * full scale — a converter reading within 0.1 dB of it has hit its own ceiling, not hers. This is the
 * one place the product refuses a recording outright, and it does not refuse HER: VOX-LAW 15 holds,
 * the take is marked seed-ineligible, it stays usable as held-out, and the session still reveals.
 */
export const TRUE_PEAK_REJECT_DBTP = -0.1;

/**
 * Minimum oversampling factor for `truePeakDbtp()`. BS.1770-4 specifies 4× for material at 48 kHz.
 * Passing less is a silent under-read of exactly the peaks the gate exists to find, so the function
 * throws rather than accepting it.
 */
export const TRUE_PEAK_MIN_OVERSAMPLE = 4;

// ─── consent (§3.8) ──────────────────────────────────────────────────────────

/**
 * "Matched" means **normalised WER at or below this**, never exact string match: a 0.6B streaming
 * FastConformer on Indian English will not produce exact matches, and an exact-match gate refuses
 * everybody.
 *
 * ⚠️ **0.35 IS PROVISIONAL AND IS NOT YET A MEASUREMENT.** PROBE-3 runs the ASR over ten real
 * Indian-English consent takes and replaces this with the measured value; §3.8 requires the
 * measurement to be recorded *next to the constant*, and this comment is that slot. Until it is
 * filled, the number matters less than it looks like it does, because the mechanism around it never
 * refuses her:
 *
 *   - `{"text":"","lang":null,"vad":"no_speech"}` is a RETRY, never a failure — a soft-spoken elderly
 *     speaker hitting Silero's VAD short-circuit must not become a consent refusal on audio a human
 *     hears perfectly.
 *   - The match is ASYNCHRONOUS. The session completes, the seed is produced, the reveal happens
 *     (VOX-LAW 15), and the voice sits in `consent-pending` until the ASR job drains. spark-1 being
 *     down delays the *fleet*, not the *family*.
 *   - Two retries, then store it, mark it `consent-unverified`, and route it to Karti for manual
 *     approval. The voice stays out of the fleet manifest exactly as VOX-LAW 22 requires. **She is
 *     never the one who gets refused.**
 *
 * measuredOn: null — PROBE-3 has not run; spark-1 is down.
 */
export const CONSENT_WER_MAX = 0.35;
export const CONSENT_WER_MEASURED_ON: string | null = null;
export const CONSENT_RETRIES = 2;

/** §3.8: a column, not a constant. The warm-up and the unscripted prompt may be any language. */
export const CONSENT_ASR_LANG_DEFAULT = "en";

// ─── the objective (§6.2, §6.3) ──────────────────────────────────────────────

/**
 * The terms the composite is allowed to contain. **A closed union, and that is the enforcement.**
 *
 * Two things are excluded BY TYPE rather than by convention, because "we agreed not to" is not a
 * mechanism:
 *
 *   - **Integrated loudness / LUFS match.** VOX-LAW 31 (see this file's header). There is no key for
 *     it, so adding one is a compile error in every consumer of `ObjectiveWeights`.
 *   - **`veCos`, Chatterbox's own voice-encoder cosine.** VOX-LAW 9: scoring a clone with the encoder
 *     that conditioned it is circular — it measures how well the model reproduced its own embedding,
 *     which it always does. **The obvious objection — that it is free, already loaded, and
 *     empirically correlates with human similarity judgements — is wrong because a circular metric
 *     that correlates is more dangerous than one that does not, since it will agree with the truth
 *     right up until the day the seed is pathological, which is the only day it was consulted.** It
 *     survives as `Diagnostics.veCos` below: stored, shown on `/admin`, and unreachable from here.
 */
export type ObjectiveTerm =
  | "snr"
  | "reverbDrr"
  | "dnsmosOvrl"
  | "utmos"
  | "f0RangeBand"
  | "speechRateBand"
  | "atypicality";

export const OBJECTIVE_TERMS: readonly ObjectiveTerm[] = [
  "snr",
  "reverbDrr",
  "dnsmosOvrl",
  "utmos",
  "f0RangeBand",
  "speechRateBand",
  "atypicality",
];

export type ObjectiveWeights = Readonly<Record<ObjectiveTerm, number>>;

/**
 * ⚠️ **THESE WEIGHTS ARE REASONING, NOT MEASUREMENT, AND §7 IS THE PHASE THAT REFITS THEM.**
 *
 * §7.4 pre-registers the rule: Spearman ρ between each seed's composite and that cell's human
 * win-rate must reach 0.5 or the weights are refit — and if SNR alone matches the composite, **the
 * rest of the vector is deleted.** Appendix D.2 lists this as one of the six decisions most likely to
 * be wrong. Do not tune these by ear on `/admin`; that is Goodharting the instrument that is supposed
 * to be measuring the product.
 *
 * The ordering of magnitudes, which is the part that IS derived:
 *
 *   `snr` and `reverbDrr` dominate because `s3gen.inference(..., n_cfm_timesteps=2)`
 *   (`tts_turbo.py:292`) gives the flow-matching decoder **two timesteps** over the Zone A reference
 *   mels — almost no capacity to move away from the reference's channel statistics. Room tone and
 *   reverb in Zone A are *reproduced*, not averaged out. That is a far stronger justification for the
 *   room check than any generic audio-quality argument, and it is why reverb is disproportionately
 *   damaging here compared with almost any other audio product. `reverbDrr` is **Zone A only**.
 *
 *   `dnsmosOvrl` and `utmos` are kept but are **within-session ranking signals only**: both are
 *   trained on degraded-speech MOS, so noise suppression and dereverberation raise them almost by
 *   construction (§4.2). Left uncontrolled, dad's laptop that ignores the `{ideal:false}` flag
 *   produces spectrally smoothed takes scoring 0.3 higher than mom's identically-good ones. `/admin`
 *   renders them grouped by `deviceFingerprint` and never compares them across devices.
 *
 *   `f0RangeBand` and `speechRateBand` are BANDS with penalties on both sides, not maxima — see
 *   `PROSODY_BANDS`.
 *
 *   `atypicality` is a PENALTY (its contribution is negative), against the speaker's own spontaneous
 *   baseline. It is the single change that converts "best-sounding window" into "most like this
 *   person", which is what the listeners are actually being asked in §7.
 */
export const OBJECTIVE_WEIGHTS: ObjectiveWeights = Object.freeze({
  snr: 0.30,
  reverbDrr: 0.22,
  dnsmosOvrl: 0.12,
  utmos: 0.10,
  f0RangeBand: 0.08,
  speechRateBand: 0.08,
  atypicality: 0.10,
});

/**
 * Typed diagnostics: computed, stored, rendered on `/admin`, and **excluded from the objective by
 * type**. Nothing in this interface may appear in `ObjectiveWeights`, and `test/wiring.test.ts`
 * asserts the two key sets are disjoint.
 */
export interface Diagnostics {
  /** VOX-LAW 9. `cos(ve_embed(seed), ve_embed(heldOut))`. The cheapest early warning that a candidate
   *  does not even land in the speaker region the model will condition on — and nothing more. */
  veCos: number;
  /** Sample peak of the window, dBFS. Reported so a disappointing clone can be explained. */
  peakDbfs: number;
  /** Measured room floor the window sat on, dBFS. §4.7 stores it on the take row for the same reason. */
  noiseFloorDb: number;
}

/**
 * **Bands, not maxima — and the failure the draft would have shipped, concretely.**
 *
 * Mom, first time, cue, prompt about the evening train. She reads it in the performed register
 * non-readers fall into when told to perform: wider F0 excursions, terminal rises, list intonation, a
 * slightly declamatory reading-to-a-child voice. **That window has the widest F0 range in the
 * session, the best DNSMOS (she projected, so SNR is higher too), and wins the composite outright. It
 * also sounds nothing like her.** The bake-off then compares it against other windows from the same
 * performed session, so the family never sees an unperformed alternative and the comparison
 * *confirms* the winner.
 *
 * Maximising prosodic variance is the wrong objective; matching it is the right one. So:
 *
 *  1. The absolute edges below are **sanity rails**, not the scoring mechanism. They exist to catch
 *     nonsense — a window of laughter, a window of a television — regardless of baseline. Adult
 *     conversational English runs ~4–6 syllables/s; the rail is opened to 2.5–7.0 so that a slow,
 *     careful 68-year-old is comfortably inside it and only genuinely degenerate windows are outside.
 *     F0 rails are wide enough to hold any adult of either sex, because a rail that encoded an
 *     expected pitch range would be a sex-specific constant in a family product.
 *  2. The actual term is `ATYPICALITY`, measured against `speakerBaseline` — the unscripted prompt
 *     (§3.7), recorded in every session anyway, which is the only recording of the person NOT
 *     performing.
 */
export const PROSODY_BANDS = Object.freeze({
  /** Syllables per second. Rails, not targets. */
  speechRate: Object.freeze({ min: 2.5, max: 7.0 }),
  /** Median F0 in Hz. Wide enough for any adult voice; this is a nonsense detector. */
  f0Median: Object.freeze({ min: 60, max: 350 }),
  /** F0 interquartile range in semitones. Below ~1 is a monotone read; above ~9 is not speech. */
  f0IqrSemitones: Object.freeze({ min: 1.0, max: 9.0 }),
});

/**
 * How far a candidate window may drift from the speaker's own spontaneous baseline before it is
 * penalised, in standard deviations of that speaker's baseline.
 *
 * §6.3 says "~1 SD" and 1.0 is that number taken literally, which is honest: it is the point at which
 * a window stops being a normal sample of how this person talks. The SDs are computed on the
 * unscripted prompt, which runs ~45 s — enough for a stable median F0 and syllable rate, not enough
 * for a precise SD, so the penalty ramps rather than switching. §7.4's ρ decides whether the whole
 * term survives.
 */
export const ATYPICALITY = Object.freeze({
  f0MedianSd: 1.0,
  f0IqrSd: 1.0,
  syllableRateSd: 1.0,
});

// ─── the scorer's identity (§6.6) ────────────────────────────────────────────

/**
 * **VOX-LAW 12. Metrics round to 4 dp before entering the envelope, and the ENVELOPE is what must be
 * byte-identical for a given `(inputSha256, scorerHash)`.**
 *
 * The draft said "two envelopes sharing `inputSha256` and `scorerHash` must have byte-identical
 * metrics". That is true on the day it is written and false the first time the ROCm venv is rebuilt:
 * MIOpen kernel autotuning, thread count and library minor versions all move the last bits of a
 * float, and onnxruntime does the same with EP and thread-count changes. `test/determinism.test.ts`
 * would fail and somebody would delete the test rather than the law.
 *
 * 4 dp because every metric here is either a cosine in [-1,1], a MOS in [1,5] or a level in dB — for
 * all three, the fourth decimal is below any difference a human or a decision in this product can
 * act on, and it is above the ~1e-6 drift a kernel change produces. A rebuild produces a **new**
 * `scorerHash` (it covers the sha256 of every model weight file, the torch / onnxruntime / ROCm
 * versions, and the thread count), so old envelopes stay valid rather than becoming wrong.
 */
export const METRIC_DECIMALS = 4;

/** `round4(0.123456)` → `0.1235`. The one rounding in the system; do it once, at the envelope. */
export const round4 = (x: number): number =>
  Number.isFinite(x) ? Math.round(x * 10 ** METRIC_DECIMALS) / 10 ** METRIC_DECIMALS : x;
